import { useQueryClient } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { cuenta, errorDelCampo, leerEnlace, MINIMO_DE_CONTRASENA } from '../api/cuenta'
import { ErrorDeLaApi } from '../api/nucleo'
import { PantallaDeAcceso } from '../componentes/Acceso'
import { Aviso, Boton, Campo } from '../componentes/ui'
import { nombreDeLaMarca } from '../marca/marca'

/**
 * Poner la contraseña con el enlace del correo: el de «¿Has olvidado tu contraseña?» (una
 * hora) o el de una invitación (siete días). El token se lee una vez y se quita de la barra
 * de direcciones, para que no se quede en el historial. Al ponerla, el núcleo deja la sesión
 * iniciada y se entra directamente.
 */
export const Restablecer = () => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const [{ token, invitacion }] = useState(() => leerEnlace(window.location.hash, window.location.search))
  const [nueva, setNueva] = useState('')
  const [repetida, setRepetida] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<unknown>(null)

  useEffect(() => {
    if (window.location.hash || window.location.search) window.history.replaceState(window.history.state, '', '/restablecer')
  }, [])

  const noCoinciden = repetida.length > 0 && nueva !== repetida

  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    if (!token || noCoinciden) return
    setEnviando(true)
    setFallo(null)
    try {
      const r = await cuenta.ponerContrasena(token, nueva)
      // Lo de antes era de otra sesión (o de nadie): se empieza limpio, ya dentro.
      clienteDeConsultas.clear()
      clienteDeConsultas.setQueryData(['sesion'], { user: r.user })
      navegar('/', { replace: true })
    } catch (error) {
      setFallo(error)
    } finally {
      setEnviando(false)
    }
  }

  if (!token) {
    return (
      <PantallaDeAcceso>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">Este enlace está incompleto</h1>
        <p className="mt-3 text-sm text-tenue">Ábrelo tal cual desde el correo, sin cortarlo. Si no funciona, pide otro: tarda un momento.</p>
        <Link className="mt-6 inline-block" to="/olvide">
          <Boton tipo="primario">Pedir otro enlace</Boton>
        </Link>
      </PantallaDeAcceso>
    )
  }

  const caducado = fallo instanceof ErrorDeLaApi && fallo.datos.codigo === 'enlace-caducado'
  const errorDeLaNueva = errorDelCampo(fallo, 'password')
  const general = fallo && !errorDeLaNueva ? (fallo instanceof ErrorDeLaApi ? fallo.message : 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.') : null

  return (
    <PantallaDeAcceso>
      <form onSubmit={enviar}>
        <span aria-hidden className="mb-5 flex size-11 items-center justify-center rounded-full bg-violeta-suave text-violeta">
          <KeyRound className="size-5" />
        </span>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">{invitacion ? 'Pon tu contraseña' : 'Pon una contraseña nueva'}</h1>
        <p className="mt-2 text-sm text-tenue">
          {invitacion ? `Es la que usarás para entrar en tu panel de ${nombreDeLaMarca()}. Al guardarla, entras.` : 'Al guardarla, entras con ella y se cierra la sesión en los demás dispositivos.'}
        </p>
        <div className="mt-8 flex flex-col gap-4">
          <Campo
            autoComplete="new-password"
            autoFocus
            ayuda={`Al menos ${MINIMO_DE_CONTRASENA} caracteres. Una frase corta que recuerdes vale más que una palabra rara.`}
            error={errorDeLaNueva}
            etiqueta="Contraseña nueva"
            minLength={MINIMO_DE_CONTRASENA}
            onChange={(e) => setNueva(e.target.value)}
            required
            type="password"
            value={nueva}
          />
          <Campo
            autoComplete="new-password"
            error={noCoinciden ? 'No coincide con la de arriba.' : undefined}
            etiqueta="Repítela"
            onChange={(e) => setRepetida(e.target.value)}
            required
            type="password"
            value={repetida}
          />
          {general && (
            <Aviso tono="error">
              {general}
              {caducado && (
                <>
                  {' '}
                  <Link className="font-medium text-violeta underline underline-offset-2" to="/olvide">
                    Pedir otro enlace
                  </Link>
                </>
              )}
            </Aviso>
          )}
          <Boton cargando={enviando} className="w-full" disabled={noCoinciden} tipo="primario" type="submit">
            {invitacion ? 'Guardar y entrar' : 'Cambiar y entrar'}
          </Boton>
        </div>
      </form>
    </PantallaDeAcceso>
  )
}
